import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Input,
} from '@mui/material';
import { ResourceDocument, ResourceDocumentFormData } from '../types/resourceDocument';
import { useAddResourceDocumentMutation, useUpdateResourceDocumentMutation } from '../api/resourceDocumentsApi';

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  resourceDocument?: ResourceDocument | null;
}

const AddEditResourceDocument: React.FC<Props> = ({ open, setModalOpen, onSuccess, resourceDocument }) => {
  const [formData, setFormData] = useState<ResourceDocumentFormData>({
    title: '',
    description: '',
    category: 'Policy',
    type: 'Policy',
    file: null,
    file_size: '',
    version: '',
    author: '',
    is_featured: false,
    published_date: '',
  });

  const [addResourceDocument] = useAddResourceDocumentMutation();
  const [updateResourceDocument] = useUpdateResourceDocumentMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categoryOptions = [
    'Organizational Policies',
    'Training Documents',
    'Program Manuals',
    'Operational Procedures',
    'Compliance & Legal',
    'Newsletter',
  ];

  const typeOptions = [
    'Policy',
    'Training',
    'Manual',
    'Procedure',
    'Framework',
    'Handbook',
    'Checklist',
  ];

  useEffect(() => {
    if (resourceDocument) {
      setFormData({
        title: resourceDocument.title || '',
        description: resourceDocument.description || '',
        category: resourceDocument.category || 'Policy',
        type: resourceDocument.type || 'Policy',
        file: null,
        file_size: resourceDocument.file_size || '',
        version: resourceDocument.version || '',
        author: resourceDocument.author || '',
        is_featured: resourceDocument.is_featured || false,
        published_date: resourceDocument.published_date || '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'Policy',
        type: 'Policy',
        file: null,
        file_size: '',
        version: '',
        author: '',
        is_featured: false,
        published_date: '',
      });
    }
    setSubmissionError(null);
  }, [resourceDocument, open]);

  const handleInputChange = (field: keyof ResourceDocumentFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      // Calculate file size
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
      handleInputChange('file', file);
      handleInputChange('file_size', `${fileSizeMB} MB`);
    } else {
      handleInputChange('file', null);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      if (!formData.title.trim()) {
        setSubmissionError('Title is required');
        setIsSubmitting(false);
        return;
      }

      if (!resourceDocument && !formData.file) {
        setSubmissionError('File is required');
        setIsSubmitting(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      formDataToSend.append('category', formData.category);
      formDataToSend.append('type', formData.type);
      
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }
      
      if (formData.file_size) {
        formDataToSend.append('file_size', formData.file_size);
      }
      if (formData.version) {
        formDataToSend.append('version', formData.version);
      }
      if (formData.author) {
        formDataToSend.append('author', formData.author);
      }
      formDataToSend.append('is_featured', formData.is_featured ? '1' : '0');
      if (formData.published_date) {
        formDataToSend.append('published_date', formData.published_date);
      }

      if (resourceDocument) {
        await updateResourceDocument({
          id: resourceDocument.id.toString(),
          body: formDataToSend,
        }).unwrap();
      } else {
        await addResourceDocument({
          body: formDataToSend,
        }).unwrap();
      }

      setModalOpen(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      setSubmissionError(
        error?.data?.message || 
        `Failed to ${resourceDocument ? 'update' : 'create'} resource document`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{resourceDocument ? 'Edit Resource Document' : 'Add New Resource Document'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {submissionError && <Alert severity="error">{submissionError}</Alert>}

          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                label="Category"
              >
                {categoryOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                label="Type"
              >
                {typeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Document File {!resourceDocument && '(Required)'}
            </Typography>
            <Input
              type="file"
              inputProps={{ accept: '.pdf,.doc,.docx' }}
              onChange={handleFileChange}
              fullWidth
            />
            {formData.file && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Selected: {formData.file.name} ({formData.file_size})
              </Typography>
            )}
            {resourceDocument && !formData.file && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Current file: {resourceDocument.file_name}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Version"
              value={formData.version}
              onChange={(e) => handleInputChange('version', e.target.value)}
              placeholder="e.g., v2.1"
              fullWidth
            />
            <TextField
              label="Author"
              value={formData.author}
              onChange={(e) => handleInputChange('author', e.target.value)}
              fullWidth
            />
          </Box>

          <TextField
            label="Published Date"
            type="date"
            value={formData.published_date}
            onChange={(e) => handleInputChange('published_date', e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_featured}
                onChange={(e) => handleInputChange('is_featured', e.target.checked)}
              />
            }
            label="Featured Document"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : resourceDocument ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditResourceDocument;


